import { Link, useRouterState } from '@tanstack/react-router';
import { Activity, Anchor, Bell, Bot, Brain, ClipboardCheck, Database, Gauge, Heart, LineChart, ListTree, ScanText, Send, Users as UsersIcon, Wrench, X } from 'lucide-react';
import { cn } from '@/lib/cn';

interface NavItem { to: string; label: string; icon: typeof Activity; }

const NAV: NavItem[] = [
  { to: '/',         label: 'Health',   icon: Activity       },
  { to: '/alerts',   label: 'Alerts',   icon: Bell           },
  { to: '/scrapers', label: 'Scrapers', icon: Bot            },
  { to: '/healer',   label: 'Healer',   icon: Wrench         },
  { to: '/dbview',   label: 'DB View',  icon: Database       },
  { to: '/deals',    label: 'Deals',    icon: ListTree       },
  { to: '/delivered', label: 'Delivered', icon: Send         },
  { to: '/anchors',  label: 'Anchors',  icon: Anchor         },
  { to: '/review',   label: 'Review',   icon: ClipboardCheck },
  { to: '/users',    label: 'Users',    icon: UsersIcon      },
  { to: '/market',   label: 'Market',   icon: LineChart      },
  { to: '/pulse',    label: 'Pulse',    icon: Gauge          },
  { to: '/signals',  label: 'Signals',  icon: Heart          },
  { to: '/ai',       label: 'Pipeline', icon: Brain          },
  { to: '/reader',   label: 'Title reader', icon: ScanText },
];

interface Props {
  /** When true, the off-canvas drawer is open on mobile. Ignored on `md+`
   *  where the sidebar is always visible inline. */
  open: boolean;
  /** Close the drawer (called on backdrop click + nav-link click on mobile). */
  onClose: () => void;
}

export default function Sidebar({ open, onClose }: Props) {
  const { location } = useRouterState();

  return (
    <>
      {/* Mobile backdrop — only renders when drawer is open. */}
      <div
        className={cn(
          'fixed inset-0 z-30 bg-slate-950/70 backdrop-blur-sm md:hidden transition-opacity',
          open ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        className={cn(
          'flex flex-col bg-slate-950/95 border-r border-slate-800',
          // Mobile: off-canvas drawer
          'fixed inset-y-0 left-0 z-40 w-64 transform transition-transform md:transition-none',
          open ? 'translate-x-0' : '-translate-x-full',
          // Desktop: static, always visible
          'md:static md:translate-x-0 md:w-56 md:shrink-0 md:bg-slate-950/60',
        )}
      >
        <div className="px-5 py-5 pt-safe border-b border-slate-800 flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <span className="dot bg-emerald-500" />
              <span className="font-semibold tracking-tight">OTR Observe</span>
            </div>
            <p className="mt-1 text-xs text-slate-500">observability + healer</p>
          </div>
          <button
            onClick={onClose}
            className="md:hidden tap-target -mr-2 rounded hover:bg-slate-800 text-slate-400 hover:text-white"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 p-2">
          {NAV.map(({ to, label, icon: Icon }) => {
            const active = to === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(to);
            return (
              <Link
                key={to}
                to={to}
                onClick={onClose}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-md text-sm transition',
                  active
                    ? 'bg-slate-800 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/50',
                )}
              >
                <Icon className="w-4 h-4" />
                {label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 pb-safe border-t border-slate-800 text-xs text-slate-500">
          v0.1.0 · {import.meta.env.MODE}
        </div>
      </aside>
    </>
  );
}
